import React, { useState } from "react";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import YouTubeEmbed, { parseYouTubeId } from "@/components/YouTubeEmbed";

/**
 * Product gallery + optional video "slide".
 * Displays a main square hero + a strip of thumbnails (images + video thumbnail).
 * Clicking a thumbnail activates it as the hero. Includes prev/next arrows.
 */
const ProductGallery = ({ images = [], videoUrl = "", brand = "AREL", testId }) => {
  const validImages = images.filter(Boolean).slice(0, 5);
  const ytId = parseYouTubeId(videoUrl);
  const slides = [
    ...validImages.map((src) => ({ type: "image", src })),
    ...(ytId ? [{ type: "video", src: videoUrl, thumb: `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` }] : []),
  ];

  const [active, setActive] = useState(0);
  if (slides.length === 0) return null;

  const clamp = (i) => (i + slides.length) % slides.length;
  const current = slides[active];
  const isDaikin = brand === "Daikin";

  return (
    <div data-testid={testId}>
      {/* Main hero */}
      <div
        className={`relative aspect-square overflow-hidden rounded-2xl border border-line ${
          current.type === "video"
            ? "bg-black"
            : isDaikin
              ? "bg-gradient-to-br from-[#e6f6fe] to-white"
              : "bg-[#efe9df]"
        }`}
        data-testid={`${testId || "product-gallery"}-hero`}
      >
        {current.type === "image" ? (
          <img
            src={current.src}
            alt="Product"
            className="h-full w-full object-cover"
          />
        ) : (
          <YouTubeEmbed
            url={current.src}
            aspectClass="aspect-square"
            className="h-full w-full"
            testId={`${testId || "product-gallery"}-video`}
          />
        )}

        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setActive((i) => clamp(i - 1))}
              aria-label="Previous"
              data-testid={`${testId || "product-gallery"}-prev`}
              className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-ink shadow ring-1 ring-black/5 hover:bg-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setActive((i) => clamp(i + 1))}
              aria-label="Next"
              data-testid={`${testId || "product-gallery"}-next`}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-ink shadow ring-1 ring-black/5 hover:bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {slides.length > 1 && (
        <div
          className="mt-4 grid grid-cols-5 gap-3"
          data-testid={`${testId || "product-gallery"}-thumbs`}
        >
          {slides.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`${testId || "product-gallery"}-thumb-${i}`}
              className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-colors ${
                i === active ? "border-[#dd0447]" : "border-line hover:border-line-strong"
              } ${
                s.type === "video"
                  ? "bg-black"
                  : isDaikin
                    ? "bg-gradient-to-br from-[#e6f6fe] to-white"
                    : "bg-[#efe9df]"
              }`}
            >
              {s.type === "image" ? (
                <img src={s.src} alt="" className="h-full w-full object-cover" />
              ) : (
                <>
                  <img src={s.thumb} alt="" className="h-full w-full object-cover opacity-90" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <PlayCircle className="h-7 w-7 text-white drop-shadow" />
                  </div>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
